import { Component, OnInit, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from '../../../services/message.service';
import { MessageRecipientUserInfo } from '../../../models/api/messageDto';
import { MessageCreateRequest } from '../../../models/api/messageCreateRequest';
import { CurrentUserState } from '../../../state/current-user.state';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { RECIPIENT_ROLE_LABELS } from '../../../utilities/message-recipients';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

@Component({
  selector: 'app-compose-message-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatChipsModule,
    MatCheckboxModule,
    NgxSpinnerModule
  ],
  templateUrl: './compose-message-dialog.component.html',
  styleUrls: ['./compose-message-dialog.component.scss']
})
export class ComposeMessageDialogComponent implements OnInit {
  composeForm!: FormGroup;
  potentialRecipients: MessageRecipientUserInfo[] = [];
  selectedFile: File | null = null;
  userRole: AppUserRole = AppUserRole.None;

  readonly AppUserRole = AppUserRole;
  readonly roleOptions = [
    { value: AppUserRole.Leader, label: RECIPIENT_ROLE_LABELS[AppUserRole.Leader]! },
    { value: AppUserRole.User, label: RECIPIENT_ROLE_LABELS[AppUserRole.User]! }
  ];

  constructor(
    public dialogRef: MatDialogRef<ComposeMessageDialogComponent>,
    private fb: FormBuilder,
    private messageService: MessageService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private currentUserState: CurrentUserState,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { companyId?: string; companyName?: string } | null
  ) {}

  ngOnInit(): void {
    this.userRole = this.currentUserState.user()?.role ?? AppUserRole.None;

    this.composeForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(200)]],
      messageText: ['', [Validators.required, Validators.maxLength(5000)]]
    });

    if (this.userRole === AppUserRole.User) {
      this.composeForm.addControl('isForAdministrator', this.fb.control(false));
    } else if (this.userRole === AppUserRole.Leader) {
      // Leaders can pick specific same-category recipients and/or also send to administrators
      this.composeForm.addControl('recipientUserIds', this.fb.control([]));
      this.composeForm.addControl('isForAdministrator', this.fb.control(false));
      this.composeForm.addValidators(this.recipientsOrAdminValidator);
      this.composeForm.updateValueAndValidity();
      this.loadRecipients();
    } else if (this.userRole === AppUserRole.Admin) {
      this.composeForm.addControl('recipientRoles', this.fb.control([]));
      this.composeForm.addControl('recipientUserIds', this.fb.control([]));
      this.loadRecipients();
    }
  }

  // Leader must either select at least one recipient or opt to send to administrators
  private readonly recipientsOrAdminValidator = (group: AbstractControl): ValidationErrors | null => {
    const recipients = (group.get('recipientUserIds')?.value as string[] | null) ?? [];
    const isForAdministrator = group.get('isForAdministrator')?.value ?? false;
    return recipients.length > 0 || isForAdministrator ? null : { recipientsRequired: true };
  };

  private loadRecipients(): void {
    this.messageService.getPotentialRecipients().subscribe({
      next: (recipients) => {
        const currentUserId = this.currentUserState.user()?.id;
        this.potentialRecipients = recipients
          .filter(r => r.userId !== currentUserId)
          .sort((a, b) => a.userFullName.localeCompare(b.userFullName));
      },
      error: (error) => {
        console.error('Error loading recipients:', error);
        this.toast.error('Error loading recipients', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`, {
        iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
      });
      input.value = '';
      return;
    }

    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  onSend(): void {
    if (this.composeForm.invalid) {
      this.composeForm.markAllAsTouched();
      return;
    }

    const formValue = this.composeForm.value;

    const request: MessageCreateRequest = {
      title: formValue.title,
      messageText: formValue.messageText,
      recipientUserIds: formValue.recipientUserIds ?? [],
      recipientRoles: formValue.recipientRoles ?? [],
      isForAdministrator: formValue.isForAdministrator ?? false,
      companyId: this.dialogData?.companyId
    };

    this.spinnerService.show();
    this.messageService.createMessage(request).pipe(
      switchMap((message) => {
        if (this.selectedFile) {
          return this.messageService.uploadAttachment(message.id, this.selectedFile);
        }
        return of(void 0);
      })
    ).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toast.success('Message sent successfully', {
          iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
        });
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error('Error sending message', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        console.error('Error sending message:', error);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
